import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Save, ClipboardList } from "lucide-react";

export const RATINGS = ["Excellent", "Good", "Average", "Needs Improvement"];

export const HW_STATUS_STYLES = {
  assigned: "bg-blue-100 text-blue-800",
  submitted: "bg-indigo-100 text-indigo-800",
  late: "bg-amber-100 text-amber-800",
  graded: "bg-emerald-100 text-emerald-800",
  missing: "bg-red-100 text-red-800",
};

export const RATING_STYLES = {
  Excellent: "bg-emerald-100 text-emerald-800",
  Good: "bg-blue-100 text-blue-800",
  Average: "bg-amber-100 text-amber-800",
  "Needs Improvement": "bg-red-100 text-red-800",
};

export function GradePill({ letter }) {
  const tone = { A: "bg-emerald-100 text-emerald-800", B: "bg-blue-100 text-blue-800", C: "bg-amber-100 text-amber-800", D: "bg-orange-100 text-orange-800", F: "bg-red-100 text-red-800" };
  if (!letter) return <span className="text-slate-400">—</span>;
  return <Badge className={`rounded-full border-0 font-bold ${tone[letter] || "bg-slate-100 text-slate-700"}`}>{letter}</Badge>;
}

export function pctOf(m, t) {
  const mm = parseFloat(m), tt = parseFloat(t);
  if (!tt || Number.isNaN(mm)) return null;
  return Math.round((mm / tt) * 10000) / 100;
}

export function letterOf(pct, bands) {
  if (pct == null) return null;
  const sorted = [...(bands || [])].sort((a, b) => b.min_score - a.min_score);
  const hit = sorted.find((b) => pct >= b.min_score && pct <= b.max_score) || sorted.find((b) => pct >= b.min_score);
  return hit?.name || null;
}

export function useGradingScale() {
  const [bands, setBands] = useState([]);
  useEffect(() => { api.get("/grading-scale?active_only=true").then((r) => setBands(r.data)).catch(() => setBands([])); }, []);
  return bands;
}

/* ------------------ Exams & Grades (teacher / manager) ------------------ */
export function ExamGradesTab() {
  const bands = useGradingScale();
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [examId, setExamId] = useState("");
  const totalMarks = 100;
  const [marks, setMarks] = useState({});
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ class_id: "", course_id: "", title: "", date: "", time: "", location: "", instructions: "" });

  const loadExams = () => api.get("/exams").then((r) => setExams(r.data));
  useEffect(() => {
    loadExams();
    api.get("/classes").then((r) => setClasses(r.data));
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => setCourses([]));
    api.get("/users?role=student").then((r) => setStudents(r.data)).catch(() => setStudents([]));
  }, []);

  const exam = exams.find((e) => e.id === examId);
  const cls = classes.find((c) => c.id === exam?.class_id);
  const roster = useMemo(
    () => students.filter((s) => cls?.student_ids?.includes(s.id)),
    [students, cls]
  );

  useEffect(() => {
    if (!examId) { setResults([]); return; }
    api.get(`/exam-results?exam_id=${examId}`).then((r) => {
      setResults(r.data);
      const m = {};
      r.data.forEach((x) => { m[x.student_id] = x.marks_obtained; });
      setMarks(m);
    }).catch(() => setResults([]));
  }, [examId]); // eslint-disable-line

  async function createExam() {
    if (!form.class_id || !form.title || !form.date) { toast.error("Class, title and date are required"); return; }
    try {
      const r = await api.post("/exams", form);
      toast.success("Exam created");
      setOpen(false);
      await loadExams();
      setExamId(r.data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function saveMarks() {
    const entries = Object.entries(marks)
      .filter(([, v]) => v !== "" && v != null)
      .map(([student_id, v]) => ({ student_id, marks_obtained: parseFloat(v) }));
    if (entries.length === 0) { toast.error("Enter marks for at least one student"); return; }
    if (entries.some((e) => Number.isNaN(e.marks_obtained) || e.marks_obtained < 0 || e.marks_obtained > 100)) {
      toast.error("Marks must be between 0 and 100"); return;
    }
    try {
      await api.post("/exam-results", { exam_id: examId, entries });
      toast.success("Marks saved");
      const r = await api.get(`/exam-results?exam_id=${examId}`);
      setResults(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap gap-3">
          <Select value={examId} onValueChange={setExamId}>
            <SelectTrigger data-testid="grade-exam-select" className="h-10 rounded-xl w-72"><SelectValue placeholder="Select exam" /></SelectTrigger>
            <SelectContent>{exams.map((e) => <SelectItem key={e.id} value={e.id}>{e.title} · {e.date}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Total marks</Label>
            <span data-testid="grade-total-marks" className="font-serif font-black text-[#0F1E4F]">100</span>
          </div>
        </div>
        <Button data-testid="exam-create-open" className="rounded-full bg-[#0F1E4F] text-white" onClick={() => { setForm({ class_id: classes[0]?.id || "", course_id: "", title: "", date: "", time: "", location: "", instructions: "" }); setOpen(true); }}>
          <Plus className="h-4 w-4 mr-1" /> New exam
        </Button>
      </div>

      {exam && (
        <Card className="p-5 rounded-2xl border-slate-200">
          <div className="text-xs uppercase tracking-widest text-[#B8860B]">{cls?.name || "—"} · {courses.find((c) => c.id === exam.course_id)?.name || "Course not set"}</div>
          <div className="mt-1 font-serif font-black text-lg text-[#0F1E4F]">{exam.title}</div>
          <div className="text-sm text-slate-600 mt-1">{exam.date} {exam.time && `· ${exam.time}`} {exam.location && `· ${exam.location}`}</div>
        </Card>
      )}

      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        {!exam ? <div className="p-10 text-center text-slate-500 text-sm">Select an exam to enter marks.</div>
          : roster.length === 0 ? <div className="p-10 text-center text-slate-500 text-sm">This class has no students yet.</div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Student</TableHead><TableHead>Marks obtained</TableHead>
                <TableHead>Percentage</TableHead><TableHead>Grade</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {roster.map((s) => {
                  const p = pctOf(marks[s.id], totalMarks);
                  return (
                    <TableRow key={s.id} data-testid={`grade-row-${s.id}`}>
                      <TableCell className="font-semibold text-[#0F1E4F]">{s.name}</TableCell>
                      <TableCell>
                        <Input data-testid={`grade-marks-${s.id}`} type="number" min="0" max="100" value={marks[s.id] ?? ""} placeholder="0–100"
                          onChange={(e) => setMarks({ ...marks, [s.id]: e.target.value })}
                          className="h-9 w-28 rounded-xl" />
                      </TableCell>
                      <TableCell data-testid={`grade-pct-${s.id}`}>{p == null ? "—" : `${p}%`}</TableCell>
                      <TableCell data-testid={`grade-letter-${s.id}`}><GradePill letter={letterOf(p, bands)} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
      </Card>
      {exam && roster.length > 0 && (
        <div className="flex justify-end">
          <Button data-testid="grade-save" onClick={saveMarks} className="rounded-full bg-[#0F1E4F] text-white">
            <Save className="h-4 w-4 mr-1.5" /> Save marks
          </Button>
        </div>
      )}

      {results.length > 0 && (
        <Card className="rounded-2xl border-slate-200 overflow-hidden">
          <div className="px-5 pt-5 font-serif font-black text-[#0F1E4F]">Saved results</div>
          <Table>
            <TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Score</TableHead><TableHead>%</TableHead><TableHead>Grade</TableHead></TableRow></TableHeader>
            <TableBody>
              {results.map((x) => (
                <TableRow key={x.id} data-testid={`result-row-${x.student_id}`}>
                  <TableCell>{students.find((s) => s.id === x.student_id)?.name || x.student_id}</TableCell>
                  <TableCell>{x.marks_obtained}/{x.total_marks}</TableCell>
                  <TableCell>{x.percentage}%</TableCell>
                  <TableCell><GradePill letter={x.letter_grade} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>New exam</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
              <SelectTrigger data-testid="exam-form-class" className="h-11 rounded-xl"><SelectValue placeholder="Class" /></SelectTrigger>
              <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
              <SelectTrigger data-testid="exam-form-course" className="h-11 rounded-xl"><SelectValue placeholder="Course / book" /></SelectTrigger>
              <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="exam-form-title" placeholder="Exam title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-11 rounded-xl" />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="exam-form-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-11 rounded-xl" />
              <Input placeholder="Time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="h-11 rounded-xl" />
            </div>
            <p className="text-xs text-slate-500">Every exam is scored out of 100 marks.</p>
            <Input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="h-11 rounded-xl" />
            <Textarea rows={3} placeholder="Instructions" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="rounded-xl" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="exam-form-save" onClick={createExam} className="rounded-full bg-[#0F1E4F] text-white">Create exam</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------ Homework grading (teacher / manager) ------------------ */
export function HomeworkGradingTab() {
  const [hw, setHw] = useState([]);
  const [classes, setClasses] = useState([]);
  const [active, setActive] = useState(null);
  const [roster, setRoster] = useState([]);
  const [gradeFor, setGradeFor] = useState(null);
  const [gform, setGform] = useState({ marks_obtained: "", total_marks: 100, feedback: "" });

  useEffect(() => {
    api.get("/homework").then((r) => setHw(r.data));
    api.get("/classes").then((r) => setClasses(r.data));
  }, []);

  async function openRoster(h) {
    setActive(h);
    const r = await api.get(`/homework/${h.id}/roster`);
    setRoster(r.data.roster);
  }

  async function saveGrade() {
    try {
      await api.post(`/homework-submissions/${gradeFor.submission.id}/grade`, {
        marks_obtained: parseFloat(gform.marks_obtained),
        total_marks: parseFloat(gform.total_marks),
        feedback: gform.feedback,
      });
      toast.success("Graded");
      setGradeFor(null);
      openRoster(active);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <div className="space-y-5">
      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        {hw.length === 0 ? <div className="p-10 text-center text-slate-500 text-sm">No homework created yet.</div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Title</TableHead><TableHead>Class</TableHead><TableHead>Due</TableHead><TableHead>Total</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {hw.map((h) => (
                <TableRow key={h.id}>
                  <TableCell className="font-semibold text-[#0F1E4F]">{h.title}</TableCell>
                  <TableCell>{classes.find((c) => c.id === h.class_id)?.name || "—"}</TableCell>
                  <TableCell>{h.due_date || "—"}</TableCell>
                  <TableCell>{h.total_marks ?? 100}</TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`hw-review-${h.id}`} variant="outline" className="rounded-full h-8" onClick={() => openRoster(h)}>
                      <ClipboardList className="h-3.5 w-3.5 mr-1" /> Review
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="rounded-2xl max-w-3xl">
          <DialogHeader><DialogTitle>{active?.title} — submissions</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Student</TableHead><TableHead>Status</TableHead><TableHead>Score</TableHead><TableHead>Grade</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {roster.map((s) => (
                  <TableRow key={s.student_id} data-testid={`hw-sub-${s.student_id}`}>
                    <TableCell className="font-semibold text-[#0F1E4F]">{s.student_name}</TableCell>
                    <TableCell><Badge className={`rounded-full border-0 capitalize ${HW_STATUS_STYLES[s.status]}`}>{s.status}</Badge></TableCell>
                    <TableCell>{s.submission?.marks_obtained != null ? `${s.submission.marks_obtained}/${s.submission.total_marks}` : "—"}</TableCell>
                    <TableCell><GradePill letter={s.submission?.letter_grade} /></TableCell>
                    <TableCell className="text-right">
                      {s.submission ? (
                        <Button data-testid={`hw-grade-${s.student_id}`} variant="outline" className="rounded-full h-8"
                          onClick={() => { setGradeFor(s); setGform({ marks_obtained: s.submission.marks_obtained ?? "", total_marks: s.submission.total_marks || active?.total_marks || 100, feedback: s.submission.feedback || "" }); }}>
                          Grade
                        </Button>
                      ) : <span className="text-xs text-slate-400">No submission</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!gradeFor} onOpenChange={(v) => !v && setGradeFor(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Grade — {gradeFor?.student_name}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            {gradeFor?.submission?.text && (
              <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700 whitespace-pre-line max-h-40 overflow-auto">{gradeFor.submission.text}</div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="hw-grade-marks" type="number" placeholder="Marks obtained" value={gform.marks_obtained} onChange={(e) => setGform({ ...gform, marks_obtained: e.target.value })} className="h-11 rounded-xl" />
              <Input data-testid="hw-grade-total" type="number" placeholder="Total marks" value={gform.total_marks} onChange={(e) => setGform({ ...gform, total_marks: e.target.value })} className="h-11 rounded-xl" />
            </div>
            <Textarea data-testid="hw-grade-feedback" rows={4} placeholder="Feedback for the student" value={gform.feedback} onChange={(e) => setGform({ ...gform, feedback: e.target.value })} className="rounded-xl" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setGradeFor(null)}>Cancel</Button>
            <Button data-testid="hw-grade-save" onClick={saveGrade} className="rounded-full bg-[#0F1E4F] text-white">Save grade</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------ Progress reports (teacher / manager) ------------------ */
export function ProgressReportsTab() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ class_id: "", student_id: "", overall_rating: "Good", report_date: "", period: "", comments: "", strengths: "", areas_for_improvement: "", recommendations: "" });

  const load = () => api.get("/progress-reports").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/classes").then((r) => setClasses(r.data));
    api.get("/users?role=student").then((r) => setStudents(r.data)).catch(() => setStudents([]));
  }, []);

  const cls = classes.find((c) => c.id === form.class_id);
  const roster = students.filter((s) => cls?.student_ids?.includes(s.id));
  const sName = (id) => students.find((s) => s.id === id)?.name || id;

  async function save() {
    try {
      if (editing) {
        const { class_id, student_id, ...rest } = form;
        await api.patch(`/progress-reports/${editing}`, rest);
      } else {
        if (!form.class_id || !form.student_id) { toast.error("Class and student are required"); return; }
        await api.post("/progress-reports", { ...form, report_date: form.report_date || null });
      }
      toast.success(editing ? "Report updated" : "Report created");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function remove(id) {
    try { await api.delete(`/progress-reports/${id}`); toast.success("Report deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Button data-testid="report-add" className="rounded-full bg-[#0F1E4F] text-white"
          onClick={() => { setEditing(null); setForm({ class_id: classes[0]?.id || "", student_id: "", overall_rating: "Good", report_date: "", period: "", comments: "", strengths: "", areas_for_improvement: "", recommendations: "" }); setOpen(true); }}>
          <Plus className="h-4 w-4 mr-1" /> Write progress report
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {list.length === 0 && <Card className="p-8 rounded-2xl border-dashed border-slate-300 text-slate-500 text-sm text-center col-span-full">No progress reports yet.</Card>}
        {list.map((p) => (
          <Card key={p.id} data-testid={`report-card-${p.id}`} className="p-6 rounded-2xl border-slate-200">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#B8860B]">{p.report_date} {p.period && `· ${p.period}`}</div>
                <div className="mt-1 font-serif font-black text-[#0F1E4F]">{sName(p.student_id)}</div>
              </div>
              <Badge className={`rounded-full border-0 ${RATING_STYLES[p.overall_rating]}`}>{p.overall_rating}</Badge>
            </div>
            <dl className="mt-3 space-y-2 text-sm">
              {[["Comments", p.comments], ["Strengths", p.strengths], ["Areas for improvement", p.areas_for_improvement], ["Recommendations", p.recommendations]].map(([k, v]) => v ? (
                <div key={k}><dt className="text-xs uppercase tracking-wider text-slate-500">{k}</dt><dd className="text-slate-700 whitespace-pre-line">{v}</dd></div>
              ) : null)}
            </dl>
            <div className="mt-4 flex gap-2">
              <Button data-testid={`report-edit-${p.id}`} variant="outline" className="rounded-full h-8"
                onClick={() => { setEditing(p.id); setForm({ ...form, ...p }); setOpen(true); }}>Edit</Button>
              <Button data-testid={`report-delete-${p.id}`} variant="outline" className="rounded-full h-8 text-red-600" onClick={() => remove(p.id)}>Delete</Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit progress report" : "New progress report"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 max-h-[65vh] overflow-auto pr-1">
            {!editing && (
              <>
                <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v, student_id: "" })}>
                  <SelectTrigger data-testid="report-class" className="h-11 rounded-xl"><SelectValue placeholder="Class" /></SelectTrigger>
                  <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                  <SelectTrigger data-testid="report-student" className="h-11 rounded-xl"><SelectValue placeholder="Student" /></SelectTrigger>
                  <SelectContent>{roster.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </>
            )}
            <Select value={form.overall_rating} onValueChange={(v) => setForm({ ...form, overall_rating: v })}>
              <SelectTrigger data-testid="report-rating" className="h-11 rounded-xl"><SelectValue placeholder="Overall rating" /></SelectTrigger>
              <SelectContent>{RATINGS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="report-date" type="date" value={form.report_date || ""} onChange={(e) => setForm({ ...form, report_date: e.target.value })} className="h-11 rounded-xl" />
              <Input placeholder="Period (e.g. Term 1)" value={form.period || ""} onChange={(e) => setForm({ ...form, period: e.target.value })} className="h-11 rounded-xl" />
            </div>
            <Textarea data-testid="report-comments" rows={3} placeholder="Teacher comments" value={form.comments || ""} onChange={(e) => setForm({ ...form, comments: e.target.value })} className="rounded-xl" />
            <Textarea rows={2} placeholder="Strengths" value={form.strengths || ""} onChange={(e) => setForm({ ...form, strengths: e.target.value })} className="rounded-xl" />
            <Textarea rows={2} placeholder="Areas for improvement" value={form.areas_for_improvement || ""} onChange={(e) => setForm({ ...form, areas_for_improvement: e.target.value })} className="rounded-xl" />
            <Textarea rows={2} placeholder="Recommendations" value={form.recommendations || ""} onChange={(e) => setForm({ ...form, recommendations: e.target.value })} className="rounded-xl" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="report-save" onClick={save} className="rounded-full bg-[#0F1E4F] text-white">Save report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------ Student progress (teacher / manager) ------------------ */
export function StudentProgressTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/progress/summaries").then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);
  const v = (x, suffix = "%") => (x == null ? "—" : `${x}${suffix}`);

  return (
    <Card data-testid="student-progress-table" className="rounded-2xl border-slate-200 overflow-x-auto">
      {loading ? <div className="p-10 text-center text-slate-500 text-sm">Loading progress…</div>
        : rows.length === 0 ? <div className="p-10 text-center text-slate-500 text-sm">No students to track yet.</div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Student</TableHead><TableHead>Class</TableHead><TableHead>Current book</TableHead>
              <TableHead>Attendance</TableHead><TableHead>Exam avg</TableHead><TableHead>HW done</TableHead>
              <TableHead>HW avg</TableHead><TableHead>Overall</TableHead><TableHead>Grade</TableHead><TableHead>Rating</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.student_id} data-testid={`progress-row-${s.student_id}`}>
                  <TableCell className="font-semibold text-[#0F1E4F]">{s.student_name}</TableCell>
                  <TableCell>{s.class_name || "—"}</TableCell>
                  <TableCell>{s.current_course || "—"}</TableCell>
                  <TableCell>{v(s.attendance_percentage)}</TableCell>
                  <TableCell>{v(s.exam_average_percentage)}</TableCell>
                  <TableCell>{v(s.homework_completion_percentage)}</TableCell>
                  <TableCell>{v(s.homework_average_grade)}</TableCell>
                  <TableCell className="font-semibold">{v(s.overall_progress)}</TableCell>
                  <TableCell><GradePill letter={s.overall_letter_grade} /></TableCell>
                  <TableCell>{s.progress_report_rating ? <Badge className={`rounded-full border-0 ${RATING_STYLES[s.progress_report_rating]}`}>{s.progress_report_rating}</Badge> : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
    </Card>
  );
}
